import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserState } from '../states/user-state';
import { UserModel } from '../../shared/models/user.model';
import { startRegistration } from '@simplewebauthn/browser';
import { startAuthentication } from '@simplewebauthn/browser';

const AUTH_HEADER_KEY = '738290';

export interface PasskeyAuthenticateOptionsResponse {
  usePasskey: boolean;
  options?: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class PasskeyApiService {
  private readonly baseUrl = '/authentication/passkey';

  constructor(
    private http: HttpClient,
    private userState: UserState
  ) {}

  private getAuthToken(): string {
    return this.userState.getAuthToken();
  }

  private authHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      [AUTH_HEADER_KEY]: token,
    });
  }

  getPasskeyCount(): Observable<{ count: number }> {
    return this.http
      .get<{ count: number }>(`${this.baseUrl}/list`, { headers: this.authHeaders() })
      .pipe(catchError((err) => throwError(() => err)));
  }

  getRegisterOptions(): Observable<unknown> {
    return this.http
      .post<unknown>(`${this.baseUrl}/register/options`, {}, { headers: this.authHeaders() })
      .pipe(catchError((err) => throwError(() => err)));
  }

  verifyRegistration(credential: unknown): Observable<{ verified: boolean }> {
    return this.http
      .post<{ verified: boolean }>(`${this.baseUrl}/register/verify`, credential, {
        headers: this.authHeaders(),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  getAuthenticateOptions(email: string): Observable<PasskeyAuthenticateOptionsResponse> {
    return this.http
      .post<PasskeyAuthenticateOptionsResponse>(`${this.baseUrl}/authenticate/options`, {
        email: email.trim().toLowerCase(),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  verifyAuthentication(assertionResponse: unknown): Observable<UserModel> {
    return this.http
      .post<UserModel>(`${this.baseUrl}/authenticate/verify`, assertionResponse)
      .pipe(catchError((err) => throwError(() => err)));
  }

  async startPasskeyRegistration(): Promise<unknown> {
    const options = await firstValueFrom(this.getRegisterOptions()).catch((err) => {
      console.error('Failed to get passkey registration options', err);
      throw new Error('Failed to get registration options');
    });
    if (!options || typeof options !== 'object') {
      throw new Error('Failed to get registration options');
    }
    const opts = options as Record<string, unknown>;
    if (opts['challenge'] === undefined || opts['challenge'] === null) {
      throw new Error('Registration options are invalid (missing challenge). Please sign in again and try adding a passkey.');
    }
    // Library expects { optionsJSON: serverOptions }, not raw serverOptions
    return startRegistration({ optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'] });
  }

  async startPasskeyAuthentication(options: unknown): Promise<unknown> {
    if (!options || typeof options !== 'object') {
      throw new Error('Invalid options');
    }
    // Library expects { optionsJSON: serverOptions }, not raw serverOptions (same as startRegistration)
    return startAuthentication({ optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'] });
  }
}
